import React, {  } from 'react';
import { FunctionComponent } from 'react';
import { FloatingLabel } from 'react-bootstrap';
import Form from 'react-bootstrap/Form';
import { Editor as TinyMceEditor } from '@tinymce/tinymce-react';
import CheckboxList, { CheckboxListLayout } from './CheckboxList';
import { getDataSourceDisplayMemberValue } from '@xFrame4/common/Utils';

export interface FieldEditorProps
{
    /** The type of the field editor. */
    type: FieldEditorType;
    /** The name of the field. */
    field: string;
    /** The label for the editor. */
    label?: string;
    /** The position of the label. */
    labelPosition?: FieldEditorLabelPosition;
    /** Is this field required? */
    isRequired?: boolean;
    /** Is this field visible? */
    isVisible?: boolean;
    /** Is the editing enabled? */
    isEnabled?: boolean;
    /** The placeholder for the field value. */
    placeholder?: string;
    /** CSS class for the control. */
    className?: string;
    /** The datasource for Select, List or listlike editors. */
    dataSource?: {[index: string]: any}[];
    /** The datasource object's property that is human readable. */
    dataSourceDisplayMember?: string;
    /** The datasource object's property that is used as the real value for the field. */
    dataSourceValueMember?: string;
    /** Allow multiple file upload for an File editor control (needs a FileList object). */
    allowMultipleFileUpload?: boolean;
    /** The maximum length of the input text. */
    maxLength?: number;
    /** The minimum value of a number, range, date, datetime-local, month, time and week field. */
    min?: string | number;
    /** The maximum value of a number, range, date, datetime-local, month, time and week field. */
    max?: string | number;
    /** Insert an element before the input element. */
    beforeInput?: JSX.Element
    /** Insert an element after the input element. */
    afterInput?: JSX.Element
    /** The value of the field. */
    value?: any;
    /** Fired when the field value changes in the editor. */
    onValueChanged?: (field: string, value: any) => void;
    /** Fired when a key is pressed down */
    onKeyDown?: (key: string) => void;
}

const FieldEditor: FunctionComponent<FieldEditorProps> = (props) =>
{
    /** Parse the input value in correct format. */
    const parseInput = (value: any) => {
        if (props.type == FieldEditorType.Number && value != null && value != '' && !isNaN(value))
        {
            return parseFloat(value);
        }
        else 
        {
            return value;
        }
    };
    
    /** Create the input control */
    let input: JSX.Element = <></>;

    if ([
        FieldEditorType.Text, 
        FieldEditorType.Email, 
        FieldEditorType.Password, 
        FieldEditorType.Number, 
        FieldEditorType.Date,
        ].includes(props.type))
    {
        input = 
        <Form.Control 
            type={props.type} 
            value={props.value ?? ''} // https://stackoverflow.com/questions/37427508/react-changing-an-uncontrolled-input
            required={props.isRequired}
            disabled={!props.isEnabled}
            placeholder={props.placeholder} 
            maxLength={props.maxLength}
            min={props.min}
            max={props.max}
            onChange={(event) => { if (props.onValueChanged) props.onValueChanged(props.field, parseInput(event.target.value)) }} 
            onKeyDown={(event) => { if (props.onKeyDown) props.onKeyDown(event.key) }}
            onWheel={(event) => (event.target as HTMLElement).blur()} // https://stackoverflow.com/questions/63224459/disable-scrolling-on-input-type-number-in-react
        />
    }
    else if (props.type == FieldEditorType.Select)
    {
        let options = props.dataSource?.map(item => 
            {
                let value = item[props.dataSourceValueMember as string];
                let display = getDataSourceDisplayMemberValue(item, props.dataSourceDisplayMember as string);

                return <option key={value} value={value}>{display}</option>
            }
        );

        let value;
        if (props.value !== null && props.value !== undefined)
        {
            // Accepts an object or a scalar
            value = typeof props.value == 'object' ? props.value[props.dataSourceValueMember!] : props.value;
        }
        
        input = 
        <Form.Select 
            aria-label={props.label} 
            value={value} 
            required={props.isRequired}
            disabled={!props.isEnabled}
            placeholder={props.placeholder}
            onChange={(event) => {
                if (props.onValueChanged) 
                {
                    return props.onValueChanged(props.field, props.dataSource?.find(item => {
                        return item[props.dataSourceValueMember!] == event.target.value // Find the selected item based on the selected value
                    }));
                }
            }}
        >
            {<option value=""></option>}
            {options}
        </Form.Select>
    }
    else if (props.type == FieldEditorType.List)
    {
        input = 
        <CheckboxList 
            dataSource={props.dataSource!} 
            dataSourceDisplayMember={props.dataSourceDisplayMember}
            dataSourceValueMember={props.dataSourceValueMember}
            disabled={!props.isEnabled}
            layout={props.labelPosition == FieldEditorLabelPosition.Floating ? CheckboxListLayout.Horizontal : CheckboxListLayout.Vertical}
            value={props.value}
            onChange={(selectedItems) => {if (props.onValueChanged) props.onValueChanged(props.field, selectedItems)}} 
        />
    }
    else if (props.type == FieldEditorType.Textarea)
    {
        input = 
        <textarea 
            className="form-control" 
            rows={6} 
            value={props.value ?? ''}
            required={props.isRequired}
            disabled={!props.isEnabled}
            placeholder={props.placeholder}
            onChange={(event) => {if (props.onValueChanged) props.onValueChanged(props.field, event.target.value)}} 
        >
        </textarea>
    }
    else if (props.type == FieldEditorType.RichTextEditor)
    {
        input = 
        <TinyMceEditor
            apiKey='imhy59jyow8r2w71xomakon8g5otmm1x8qexd2u13ejqoxdm'
            value={props.value ?? ''}
            init={{
                height: 500,
                placeholder: props.placeholder,
                menubar: true,
                plugins: [
                    'advlist', 'autolink', 'lists', 'link', 'image', 'charmap', 'preview',
                    'anchor', 'searchreplace', 'visualblocks', 'code', 'fullscreen',
                    'insertdatetime', 'media', 'table', 'code', 'help', 'wordcount'
                ],
                toolbar: 'undo redo | blocks | ' +
                    'bold italic forecolor | alignleft aligncenter ' +
                    'alignright alignjustify | bullist numlist outdent indent | ' +
                    'removeformat | help',
                content_style: 'body { font-family:Helvetica,Arial,sans-serif; font-size:14px }'
            }}
            onEditorChange={(content, editor) => {if (props.onValueChanged) props.onValueChanged(props.field, content)}}
        />
    }
    else if (props.type == FieldEditorType.Checkbox)
    {
        input = 
        <Form.Check 
            type="checkbox"
            label={props.label}
            checked={props.value ?? false}
            className="d-flex align-items-center"
            onChange={(event) => {if (props.onValueChanged) props.onValueChanged(props.field, event.target.checked)}} 
        />
    }
    else if (props.type == FieldEditorType.File)
    {
        input = 
        <Form.Control 
            type="file"
            required={props.isRequired}
            disabled={!props.isEnabled}
            placeholder={props.placeholder} 
            multiple={props.allowMultipleFileUpload}
            onChange={(event) => fileChangeHandler(event)} 
        />
    }

    /** Selected file changed. */
    const fileChangeHandler = (event: any) => 
    {
        if (event.target.files != null && event.target.files.length > 0)
        {
            if (props.onValueChanged !== undefined)
            {
                props.onValueChanged(props.field, !props.allowMultipleFileUpload ? event.target.files[0] : event.target.files);
            }
        }
    };

    /** Add label */
    let cmpInputWithLabel = <></>;
    switch (props.labelPosition)
    {
        case FieldEditorLabelPosition.Horizontal:
            {
                cmpInputWithLabel = 
                <Form.Group 
                    controlId={'field-editor-' + props.field}
                >
                    {props.type != FieldEditorType.Checkbox && <Form.Label>{props.label}</Form.Label>}
                    {props.beforeInput}
                    {input}
                    {props.afterInput}
                </Form.Group>
            }
            break;
        case FieldEditorLabelPosition.Floating:
            {
                cmpInputWithLabel = 
                <FloatingLabel 
                    controlId={'field-editor-' + props.field} 
                    label={props.type != FieldEditorType.Checkbox && props.label}
                >
                    {props.beforeInput}
                    {input}
                    {props.afterInput}
                </FloatingLabel>
            }
            break;
        case FieldEditorLabelPosition.Hidden:
            {
                cmpInputWithLabel = 
                <Form.Group 
                    controlId={'field-editor-' + props.field}
                >
                    {props.beforeInput}
                    {input}
                    {props.afterInput}
                </Form.Group>
            }
            break;
    }

    /** Render */
    return (
        <div className={`field-editor field-editor-${props.type} ${props.className}`} style={{display: props.isVisible ? 'block' : 'none'}}>
            {cmpInputWithLabel}
        </div>
    );
}

export enum FieldEditorType
{
    Text = 'text',
    Email = 'email',
    Password = 'password',
    Number = 'number',
    /** Accepts a list of objects. Single selection. */
    Select = 'select',
    /** Accepts a list of objects. Multiple selection. */
    List = 'list',
    Date = 'date',
    Textarea = 'textarea',
    RichTextEditor = 'rich-text-editor',
    Checkbox = 'checkbox',
    File = 'file',
}

export enum FieldEditorLabelPosition
{
    Vertical,
    Horizontal,
    Floating,
    Hidden
}

FieldEditor.defaultProps = 
{
    labelPosition: FieldEditorLabelPosition.Horizontal,
    dataSourceValueMember: 'value',
    dataSourceDisplayMember: 'display',
    isRequired: false,
    isVisible: true,
    isEnabled: true,
    allowMultipleFileUpload: false,
    className: ''
}

/**
 * Converts an array of strings to a datasource array.
 * 
 * @param stringArray 
 */
export function arrayToDataSource(stringArray: string[])
{
    let dataSource: any[] = [];

    for (let str of stringArray)
    {
        let dataSourceRow = {
            value: str,
            display: str
        };
        dataSource.push(dataSourceRow);
    }

    return dataSource;
}

/**
 * Converts an enum to a datasource array.
 * 
 * @param enumObject The object that needs to be converted.
 * @param t A function to translate the key for display.
 */
export function enumToDataSource(enumObject: any, t?: (text: string) => string)
{
    let dataSource: any[] = [];

    for (let [key, value] of Object.entries(enumObject))
    {
        let dataSourceRow = {
            value: value,
            display: t ? t(key) : key
        };
        dataSource.push(dataSourceRow);
    }

    return dataSource;
}

export default FieldEditor;